import {
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Switch,
    Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { PodDto } from "../../../../w2d2d-api-client.ts";
import { Add, Delete } from "@mui/icons-material";
import { useLoadingControl } from "../../../../../appfw/loading/use-loading-control.tsx";
import DialogActionButton from "../../../../../appfw/ui/dialogs/DialogActionButton.tsx";
import AppFwOverlay from "../../../../../appfw/ui/layouts/AppFwOverlay.tsx";
import LoadingScreen from "../../../../../appfw/ui/loading/loading-screen.tsx";
import { usePodsControl } from "../../../hooks/use-pods-control.tsx";
import AppFwFab from "../../../../../appfw/ui/buttons/AppFwFab.tsx";
import { TextInputDialog } from "../../../../../appfw/ui/dialogs/TextInputDialog.tsx";

export default function PodsSettingsPage() {

    const loadingControl = useLoadingControl("pods-settings")
    const { pods, addPod, enablePod, disablePod, deletePod } = usePodsControl()

    const [showAddDialog, setShowAddDialog] = useState(false)

    const handleAddPod = useCallback(async (name: string | null) => {
        setShowAddDialog(false)
        if (!name) return
        await loadingControl.withLoading(async () => {
            await addPod(name)
        })
    }, [loadingControl, addPod])

    const handleTogglePod = useCallback(async (pod: PodDto) => {
        await loadingControl.withLoading(async () => {
            if (pod.enabled) {
                await disablePod(pod.name)
            } else {
                await enablePod(pod.name)
            }
        })
    }, [loadingControl, enablePod, disablePod])

    const handleDeletePod = useCallback(async (pod: PodDto) => {
        await loadingControl.withLoading(async () => {
            await deletePod(pod.name)
        })
    }, [loadingControl, deletePod])

    if (!pods) {
        return (
            <AppFwOverlay title="Pods verwalten">
                <LoadingScreen />
            </AppFwOverlay>
        )
    }

    return (
        <AppFwOverlay title="Pods verwalten">

            <Stack spacing={2} sx={{ padding: 2 }}>

                <List>
                    {pods.map((pod: PodDto) => (
                        <ListItem
                            key={pod.name}
                            secondaryAction={
                                <Stack direction="row" alignItems="center">
                                    <Switch
                                        edge="end"
                                        onChange={() => handleTogglePod(pod)}
                                        checked={pod.enabled}
                                        disabled={loadingControl.isLoading}
                                    />
                                    <DialogActionButton
                                        outlet={
                                            <IconButton edge="end" aria-label="delete" disabled={loadingControl.isLoading}>
                                                <Delete />
                                            </IconButton>
                                        }
                                        dialogTitle={`Pod "${pod.name}" löschen?`}
                                        dialogContent={"Möchtest du diesen Pod wirklich löschen?"}
                                        dialogActions={[
                                            { label: "Abbrechen" },
                                            {
                                                label: "Löschen",
                                                action: async () => handleDeletePod(pod)
                                            }
                                        ]}
                                    />
                                </Stack>
                            }
                        >
                            <ListItemText
                                primary={pod.name}
                                secondary={pod.enabled ? "Aktiviert" : "Deaktiviert"}
                            />
                        </ListItem>
                    ))}
                    {pods.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", padding: 2 }}>
                            Keine Pods vorhanden.
                        </Typography>
                    )}
                </List>
            </Stack>

            <AppFwFab aria-label="Pod hinzufügen" onClick={() => setShowAddDialog(true)}>
                <Add />
            </AppFwFab>
            <TextInputDialog
                open={showAddDialog}
                dialogTitle="Pod hinzufügen"
                inputLabel="Pod Name"
                onClose={handleAddPod}
                confirmText="Hinzufügen"
            />
        </AppFwOverlay>
    )
}
